import React from "react";
import { RootLayouts } from "@/src/Layouts";
import CourseLevel from "@/src/Components/CoursePageV2/Components/CourseLevel";
import CourseListingPage from "@/src/Components/CoursePageV2/Components/CourseListingPage";
import { apiRequest } from "@/utils/apiHelper";
import { buildQueryString } from "@/utils/buildQueryString";
import {
  courseQueryParams,
  specializationQueryParams,
  streamsQueryParams,
  topCourseQueryParams,
} from "@/utils/coursePageQuery/courseLandingPageQueryParams";
import dynamic from "next/dynamic";
import {
  getCourseFilterPageCache,
  setCourseFilterPageCache,
} from "@/utils/coursePageQuery/courseFilterDataCache";
// import { fetchFooterData } from "@/utils/commanQuery/fetchFooterData";
import { generateCourseFilterPageSEOData } from "@/src/utils/seo-utils/course/seoCourseFilterUtils";
import { SEOHead } from "@/src/Components/seo";

const CoursePage = ({
  courseId,
  course,
  specialization,
  filter,
  topCourseData,
  seoData,
  queryParams,
}: any) => {
  const TopExploreSection = dynamic(
    () => import("@/src/Components/common/TopExploreSection"),
    { ssr: true }
  );
  const CourseListSection = dynamic(
    () => import("@/src/Components/CoursePageV2/CourseListComponent"),
    { ssr: false }
  );

  if (!courseId) {
    return <div>Loading...</div>;
  }

  return (
    <RootLayouts>
      <SEOHead seoData={seoData} isIndex={false} />
      <TopExploreSection
        title="Courses"
        data={topCourseData
          ?.filter((course: any) => course?.page_data?.length > 0)
          ?.map((course: any, index: number) => ({
            name: course?.abbreviation,
            url: course?.course_url,
            id: `${course?.id}-${index}`,
          }))}
        linkPrefix="/courses"
        linkSuffix="/course-info"
        className="bg-gray-200 py-1"
        textColor="text-black"
      />
      <section className="mt-2 lg:mt-5 mb-10 min-h-40">
        {/* /courses/course-after-10th */}
        {filter === "course-level" ? (
          <CourseLevel streamData={course?.results} courseId={courseId} />
        ) : (
          <section className="lg:w-[85%] 2xl:w-4/5 lg:max-w-screen-xl mx-auto font-poppins">
            <p className="text-sm md:text-base">
              Showing{" "}
              <span className="text-primary font-semibold">
                {course?.pagination?.total}
              </span>{" "}
              Courses
            </p>
            <CourseListingPage
              courseData={course?.results}
              specializationData={specialization}
            />
            <CourseListSection
              queryParam={queryParams}
              pageCount={course?.pagination?.pageCount}
            />
          </section>
        )}
      </section>
    </RootLayouts>
  );
};

export default CoursePage;

async function fetchDataWithCache(
  cacheKey: string,
  queryParams: any,
  endpoint: string
) {
  const inMemoryConfig = JSON.parse(
    process.env.NEXT_PUBLIC_IN_MEMORY_CACHE ?? "{}"
  );

  const shouldUseCache =
    inMemoryConfig?.NEXT_PUBLIC_IN_MEMORY_CACHE_COURSE_PAGE === "true";

  // ✅ If caching is enabled, check cache first
  if (shouldUseCache) {
    const cachedData = getCourseFilterPageCache(cacheKey);
    if (cachedData) {
      console.log(`[Cache Hit] ${endpoint} data retrieved from cache.`);
      return cachedData;
    }
  }

  // ✅ Always fetch from API if cache is disabled or cache miss occurs
  console.log(`[Cache Miss] Fetching ${endpoint} from API...`);
  try {
    const response = await apiRequest(
      `/${endpoint}?${buildQueryString(queryParams)}`,
      "GET"
    );
    const data = response?.data?.data?.results ? response?.data?.data : [];

    // ✅ Set cache only if caching is enabled
    if (shouldUseCache && data?.results?.length > 0)
      setCourseFilterPageCache(cacheKey, data);

    return data;
  } catch (error) {
    console.error(`[Fetch Error] Failed to fetch ${endpoint}:`, error);
    return [];
  }
}
export async function getServerSideProps(context: any) {
  const { courseId } = context.params;

  const normalizeString = (str: string) =>
    str?.replace(/-/g, " ").replace(/&/g, " & ").toLowerCase();

  const topCourseData = await fetchDataWithCache(
    JSON.stringify(topCourseQueryParams),
    topCourseQueryParams,
    "courses"
  );

  // const footerData = await fetchFooterData();

  /** STEP 1: Check for Stream match first **/
  const courseQueryParam = {
    ...courseQueryParams,
    filters: {
      streams: { stream_name: { $eqi: normalizeString(courseId) } },
    },
  };

  const streamData = await fetchDataWithCache(
    JSON.stringify(courseQueryParam),
    courseQueryParam,
    "courses"
  );

  if (streamData?.results?.length > 0) {
    return {
      props: {
        courseId: courseId ?? null,
        course: streamData ?? null,
        specialization: null,
        filter: "stream",
        topCourseData: topCourseData?.results,
        // footerData: footerData,
        queryParams: courseQueryParam,
        seoData: generateCourseFilterPageSEOData(
          normalizeString(courseId),
          "stream"
        ),
      },
    };
  }

  /** STEP 2: Check for Course-Level match if Course match fails **/
  const streamQueryParamsOptimized = {
    ...streamsQueryParams,
    filters: {
      course_levels: { course_level_url: { $eq: courseId } },
    },
    populate: {
      ...streamsQueryParams.populate,
      courses: {
        ...streamsQueryParams.populate.courses,
        filters: {
          course_level: { course_level_url: { $eq: courseId } },
        },
      },
      specializations: {
        ...streamsQueryParams.populate.specializations,
        filters: {
          course_level: { course_level_url: { $eq: courseId } },
        },
      },
    },
  };

  const courseLevelData = await fetchDataWithCache(
    JSON.stringify(streamQueryParamsOptimized),
    streamQueryParamsOptimized,
    "streams"
  );

  if (courseLevelData?.results?.length > 0) {
    return {
      props: {
        courseId: courseId ?? null,
        course: courseLevelData ?? null,
        specialization: null,
        filter: "course-level",
        topCourseData: topCourseData?.results,
        // footerData: footerData,
        queryParams: streamQueryParamsOptimized,
        seoData: generateCourseFilterPageSEOData(
          normalizeString(courseId),
          "course-level"
        ),
      },
    };
  }

  /** STEP 4: Handle Specialization (Stream + Course-Level match) **/
  const splitIndex = courseId.lastIndexOf("-courses");
  if (splitIndex !== -1) {
    const streamPart = courseId.substring(0, splitIndex);
    const courseLevelPart = courseId.substring(splitIndex + 1);

    const courseQueryParamsNested = {
      ...courseQueryParams,
      filters: {
        $and: [
          { streams: { stream_name: { $eqi: normalizeString(streamPart) } } },
          { course_level: { course_level_url: { $eq: courseLevelPart } } },
        ],
      },
    };

    const specializationQueryParamsNested = {
      ...specializationQueryParams,
      filters: {
        $and: [
          { streams: { stream_name: { $eqi: normalizeString(streamPart) } } },
          { course_level: { course_level_url: { $eq: courseLevelPart } } },
        ],
      },
    };

    const [nestedCourseData, nestedSpecializationData] = await Promise.all([
      fetchDataWithCache(
        JSON.stringify(courseQueryParamsNested),
        courseQueryParamsNested,
        "courses"
      ),
      fetchDataWithCache(
        JSON.stringify(specializationQueryParamsNested),
        specializationQueryParamsNested,
        "specializations"
      ),
    ]);

    if (
      nestedCourseData?.results?.length > 0 ||
      nestedSpecializationData?.results?.length > 0
    ) {
      return {
        props: {
          courseId: courseId ?? null,
          course: nestedCourseData ?? null,
          specialization: nestedSpecializationData ?? null,
          filter: "combination",
          topCourseData: topCourseData?.results,
          // footerData: footerData,
          queryParams: courseQueryParamsNested,
          seoData: generateCourseFilterPageSEOData(
            normalizeString(courseId),
            "combination"
          ),
        },
      };
    }
  }

  /** STEP 5: No match found **/
  return {
    notFound: true,
  };
}
